/**
 * Shared HTML→plain-text reducer behind two named presets that reproduce the
 * exact (bit-for-bit) output of the two historical implementations they
 * replace:
 *   - `'email'` — was `@wit-support/admin-contracts` `email-layout.ts`
 *     `htmlToPlainText` (derives the plain-text fallback body for outbound
 *     email alongside the HTML body).
 *   - `'llm'` — was `@wit-support/db` `message-translation-service.ts`
 *     `htmlToPlainText` (derives a translatable plain-text body for
 *     HTML-only inbound messages).
 *
 * The two copies diverged in behavior (script/style stripping, which block
 * tags close to a newline, the tag-removal replacement character, entity
 * casing sensitivity, numeric-entity range guarding, whitespace collapsing)
 * enough that a single merged pass would change one side's output — see
 * `.superpowers/sdd/06-backend-dry/task-4-brief.md`. Each preset below is a
 * literal port of its historical regex sequence, staged in the same relative
 * order as the original. This module is the single source of truth; the two
 * old call sites now forward to it unchanged (see `email-layout.ts` and
 * `message-translation-service.ts`).
 *
 * Pure — no I/O.
 */
// --- 'email' preset stages — verbatim port of the old admin-contracts regex chain ---
const emailBrToNewline = (html) => html.replace(/<\s*br\s*\/?\s*>/gi, '\n');
const emailBlockCloseToNewline = (html) => html.replace(/<\s*\/(p|div|h[1-6]|li|ul|ol|blockquote|tr|td)\s*>/gi, '\n');
const emailHrToRule = (html) => html.replace(/<\s*hr\s*\/?\s*>/gi, '\n---\n');
const emailLiOpenToBullet = (html) => html.replace(/<\s*li[^>]*>/gi, '• ');
const emailStripTags = (html) => html.replace(/<[^>]+>/g, '');
const emailDecodeEntities = (html) => html
    .replace(/&nbsp;/g, ' ')
    .replace(/&mdash;/g, '—')
    .replace(/&ldquo;/g, '"')
    .replace(/&rdquo;/g, '"')
    .replace(/&middot;/g, '·')
    .replace(/&copy;/g, '©')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
const emailStages = [
    emailBrToNewline,
    emailBlockCloseToNewline,
    emailHrToRule,
    emailLiOpenToBullet,
    emailStripTags,
    emailDecodeEntities,
];
// --- 'llm' preset stages — verbatim port of the old db regex chain ---
const llmStripScriptStyle = (html) => html.replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ');
const llmBlockCloseToNewline = (html) => html.replace(/<\/(p|div|li|tr|h[1-6]|blockquote)>/gi, '\n');
const llmBrToNewline = (html) => html.replace(/<br\s*\/?>/gi, '\n');
const llmStripTags = (html) => html.replace(/<[^>]+>/g, ' ');
const llmDecodeEntities = (html) => html
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&#(\d+);/g, (m, n) => {
    const code = Number(n);
    // Only decode a VALID Unicode code point. An out-of-range numeric entity
    // (e.g. &#99999999;) passes Number.isFinite but makes String.fromCodePoint
    // throw RangeError — which would crash the whole translation. Leave such a
    // malformed entity as its literal text instead of throwing/discarding.
    return Number.isInteger(code) && code >= 0 && code <= 0x10ffff
        ? String.fromCodePoint(code)
        : m;
});
const llmCollapseSpaces = (html) => html.replace(/[ \t\f\v]+/g, ' ');
const llmTrimAroundNewlines = (html) => html.replace(/[ \t]*\n[ \t]*/g, '\n');
const llmStages = [
    llmStripScriptStyle,
    llmBlockCloseToNewline,
    llmBrToNewline,
    llmStripTags,
    llmDecodeEntities,
    llmCollapseSpaces,
    llmTrimAroundNewlines,
];
// --- shared final stages — identical regex in both originals ---
const collapseBlankLines = (html) => html.replace(/\n{3,}/g, '\n\n');
const trimStage = (html) => html.trim();
const STAGES = {
    email: [...emailStages, collapseBlankLines, trimStage],
    llm: [...llmStages, collapseBlankLines, trimStage],
};
/**
 * Convert HTML to readable plain text, staged per preset so the two
 * historically-diverged callers keep their exact original output:
 *   - `'email'` — bullets (`• `) for `<li>`, `<hr>` → `\n---\n`, a wider
 *     block-tag set (adds `ul`/`ol`/`td`), tags removed with `''`,
 *     case-sensitive named entities (`&mdash;`, `&ldquo;`, `&middot;`, …),
 *     no script/style stripping, no numeric-entity decoding.
 *   - `'llm'` — script/style content stripped first, tags removed with
 *     `' '` (a space, not `''`), case-insensitive entities, numeric-entity
 *     decoding guarded against out-of-range code points (kept literal
 *     instead of throwing), and full whitespace collapsing.
 * Pure — no I/O.
 */
export function htmlToPlainText(html, preset) {
    return STAGES[preset].reduce((acc, stage) => stage(acc), html);
}
//# sourceMappingURL=html-to-plain-text.js.map